import { cn } from '@/components/ui';
import { layers, radii, shadows, spacingSteps } from '../tokens';
import { Code, Section } from './Section';

export function SpacingSection() {
  return (
    <Section
      id="space"
      title="Отступы и размеры"
      lead="Сетка 4 px, шкала закрыта на уровне темы: класса px-2.5 не существует. Подшаги 0.5 и 1.5 — для пары «иконка ↔ текст»; 3.5 и 4.5 — только размеры иконок (R7)."
    >
      <div className="flex flex-col gap-2 rounded-lg border border-line bg-card p-5">
        {spacingSteps.map((s) => (
          <div key={s.step} className="grid grid-cols-[4rem_3rem_1fr_6rem] items-center gap-3 text-xs">
            <Code>{s.step}</Code>
            <span className="font-mono text-fg-subtle">{s.px}px</span>
            <div className="h-3 rounded-xs bg-brand" style={{ width: s.px }} aria-hidden />
            <span className="text-fg-subtle">{s.note ?? ''}</span>
          </div>
        ))}
      </div>
      <p className="text-sm text-fg-muted">
        Высоты контролов: <Code>h-8</Code> sm · <Code>h-9</Code> md · <Code>h-11</Code> lg — в одной строке
        один размер (R11).
      </p>
    </Section>
  );
}

export function RadiusSection() {
  return (
    <Section
      id="radius"
      title="Радиусы"
      lead="Закрытая шкала 4 → 24 px. Правило вложенности R8: внутренний элемент не круглее контейнера — холст 16 ≥ карточка 16 ≥ контрол 12 ≥ элемент внутри контрола 8."
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col divide-y divide-line rounded-lg border border-line bg-card">
          {radii.map((r) => (
            <div
              key={r.utility}
              className="grid grid-cols-[3rem_9rem_3rem_1fr] items-center gap-3 px-5 py-3 text-xs"
            >
              <div className={cn('size-10 border border-line-strong bg-muted', r.utility)} aria-hidden />
              <Code>{r.utility}</Code>
              <span className="font-mono text-fg-subtle">{r.px === 9999 ? '∞' : `${r.px}px`}</span>
              <span className="text-fg-muted">{r.usage}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg bg-frame p-3">
          <div className="rounded-lg bg-canvas p-6 shadow-inner-card">
            <div className="text-2xs text-fg-subtle">холст · rounded-lg</div>
            <div className="mt-2 rounded-lg border border-line bg-card p-4">
              <div className="text-2xs text-fg-subtle">карточка · rounded-lg</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex h-9 items-center rounded-md border border-line-strong bg-card px-3 text-xs text-fg">
                  поле · rounded-md
                </div>
                <div className="flex h-9 items-center rounded-md bg-brand-hover px-3 text-xs text-on-brand">
                  кнопка
                </div>
                <span className="rounded-full bg-success px-2 py-0.5 text-2xs font-semibold text-on-success">
                  плашка
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

export function ShadowsSection() {
  return (
    <Section
      id="shadow"
      title="Тени и глубина"
      lead="Пять ступеней высоты плюс именованные рецепты. Значения переключаются с темой: в тёмной тени работают как свечение и внутренняя подсветка. Inline shadow-[…] запрещены."
    >
      <div className="grid grid-cols-2 gap-6 rounded-lg bg-canvas p-6 md:grid-cols-3 lg:grid-cols-4">
        {shadows.map((s) => (
          <div
            key={s.utility}
            className={cn(
              'flex h-24 flex-col justify-end rounded-lg border border-line bg-card p-3',
              s.utility,
            )}
          >
            <Code>{s.utility}</Code>
            <span className="mt-1 text-2xs text-fg-subtle">{s.usage}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function LayersSection() {
  return (
    <Section
      id="layers"
      title="Слои"
      lead="z-index только именами (R12). Поповеры выше модалок — меню внутри диалога работают; тултипы выше всего."
    >
      <div className="overflow-hidden rounded-lg border border-line bg-card">
        <table className="w-full text-left text-sm">
          <thead className="type-overline text-fg-subtle">
            <tr className="border-b border-line">
              <th className="px-4 py-3 font-semibold">утилита</th>
              <th className="px-4 py-3 font-semibold">z</th>
              <th className="px-4 py-3 font-semibold">что</th>
            </tr>
          </thead>
          <tbody>
            {layers.map((l) => (
              <tr key={l.utility} className="border-b border-line last:border-b-0">
                <td className="px-4 py-2">
                  <Code>{l.utility}</Code>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-fg-muted">{l.z}</td>
                <td className="px-4 py-2 text-fg">{l.usage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
